import type { GridRowId } from "@mui/x-data-grid";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import { useId, useState } from "react";
import {
  searchUsersByFullNameOrLocker,
  type Usuario,
} from "../../firebase/firestore/usuarios";
import type Fee from "../../interfaces/Fee";
import {
  EntregaIncompletaError,
  updatePackagesInBatch,
} from "../../firebase/firestore/paquetes";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { describirCamposFaltantes } from "../../utils/entregaValidations";
import { useEstados } from "../../utils/estadosUtils";
import AutocompleteComponent from "../inputs/Autocomplete";
import { usePaqueteStore } from "../../zustand/usePaquetesStore";

export default function FormPackagesInBatch({
  onClose,
  ids,
}: {
  onClose: () => void;
  ids?: Set<GridRowId>;
}) {
  const { agencia } = useAgenciaStore();
  const tarifas = agencia?.tarifas || [];
  const estadoLabelId = useId();
  const estadoSelectId = useId();
  const viaLabelId = useId();
  const viaSelectId = useId();
  const tarifaLabelId = useId();
  const tarifaSelectId = useId();
  const [selectedField, setSelectedField] = useState<
    | "estado"
    | "idUsuario"
    | "via"
    | "tarifa"
    | "cobroAdicional"
    | "envio"
    | "ubicacionFisica"
    | null
  >(null);
  const { fetchAllPaquetes, fetchCounts } = usePaqueteStore();

  const [estado, setEstado] = useState("");
  const [cliente, setCliente] = useState<Usuario>();
  const [via, setVia] = useState("");
  const [tarifa, setTarifa] = useState<Fee>();
  const [cobroAdicional, setCobroAdicional] = useState<number>(0);
  const [envio, setEnvio] = useState<string>("");
  const [ubicacionFisica, setUbicacionFisica] = useState<string>("");
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = async (_: any, newValue: string) => {
    if (newValue.length >= 2) {
      const usuarios = await searchUsersByFullNameOrLocker(newValue);
      setUsuarios(usuarios || []);
    }
  };

  const handleBatchUpdate = async () => {
    if (!ids || ids?.size === 0) {
      toast.error("Debe seleccionar al menos un paquete");
      return;
    }
    
    if (!selectedField) {
      toast.error("Debe seleccionar un campo para editar");
      return;
    }

    setIsLoading(true);

    try {
      const idsString = Array.from(ids).map(String);
      const value =
        selectedField === "estado"
          ? estado
          : selectedField === "idUsuario"
          ? cliente?.id
          : selectedField === "via"
          ? via
          : selectedField === "tarifa"
          ? tarifa
          : selectedField === "cobroAdicional"
          ? cobroAdicional
          : selectedField === "envio"
          ? (envio?.trim() || null)
          : selectedField === "ubicacionFisica"
          ? (ubicacionFisica?.trim() || null)
          : "";

      // Validar que el valor no esté vacío para campos requeridos
      // (envio y ubicacionFisica pueden ser null para eliminar)
      if (selectedField !== "envio" && selectedField !== "ubicacionFisica" && !value) {
        toast.error(`Debe ingresar un valor para ${selectedField}`);
        setIsLoading(false);
        return;
      }

      await updatePackagesInBatch({
        ids: idsString,
        field: selectedField,
        value,
        estadosEntregado: agencia?.estadosEntregado ?? [],
      });
      toast.success(`${ids.size} paquete(s) actualizado(s) con éxito`);
      onClose();
      await Promise.all([fetchAllPaquetes(), fetchCounts()]);
    } catch (error) {
      if (error instanceof EntregaIncompletaError) {
        const detalle = error.paquetes
          .slice(0, 5)
          .map(
            (paquete) =>
              `${paquete.idRastreo} (falta ${describirCamposFaltantes(
                paquete.faltantes
              )})`
          )
          .join(", ");
        const restantes = error.paquetes.length - 5;

        toast.error(
          `No se marcaron como entregados: ${detalle}${
            restantes > 0 ? ` y ${restantes} más` : ""
          }. Registre peso y tarifa antes de entregar.`
        );
        return;
      }

      console.error("Error al actualizar paquetes:", error);
      toast.error(`Error al actualizar paquetes. Intente nuevamente`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Divider />
      <Box display="flex" flexDirection="column" gap={2} mt={2} px={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          Selecciona un campo para editar
        </Typography>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Cambiar estado
          </Typography>
          <FormControl
            sx={{ width: "80%" }}
            variant="outlined"
            disabled={selectedField !== null && selectedField !== "estado"}
            size="small"
          >
            <InputLabel id={estadoLabelId}>Estado</InputLabel>
            <Select
              value={estado}
              onChange={(e) => {
                setSelectedField("estado");
                setEstado(e.target.value);
              }}
              labelId={estadoLabelId}
              id={estadoSelectId}
              label="Estado"
              size="small"
            >
              {useEstados().map((estado) => (
                <MenuItem key={estado.id} value={estado.id}>
                  {estado.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Cambiar usuario
          </Typography>
          <AutocompleteComponent
            options={usuarios}
            value={cliente}
            disabled={selectedField !== null && selectedField !== "idUsuario"}
            noOptionsText="No hay registros disponibles"
            size="small"
            getOptionLabel={(user) =>
              `${user?.name} ${user?.lastName} — ${user?.lockerCode}`
            }
            filterOptions={(x) => x}
            onInputChange={(e, value) => {
              setSelectedField("idUsuario");
              handleInputChange(e, value);
            }}
            onChange={(_, value) => {
              setSelectedField("idUsuario");
              if (value) setCliente(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar por nombre, apellido o código de cliente"
              />
            )}
            sx={{ width: "80%" }}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Cambiar vía
          </Typography>
          <FormControl
            sx={{ width: "80%" }}
            disabled={selectedField !== null && selectedField !== "via"}
            variant="outlined"
            size="small"
          >
            <InputLabel id={viaLabelId}>Vía</InputLabel>
            <Select
              value={via}
              onChange={(e) => {
                setSelectedField("via");
                setVia(e.target.value);
              }}
              labelId={viaLabelId}
              id={viaSelectId}
              label="Vía"
            >
              <MenuItem value="maritimo">Marítimo</MenuItem>
              <MenuItem value="aereo">Aéreo</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Cambiar Tarifa
          </Typography>
          <FormControl
            sx={{ width: "80%" }}
            disabled={selectedField !== null && selectedField !== "tarifa"}
            variant="outlined"
            size="small"
          >
            <InputLabel id={tarifaLabelId}>Tarifa</InputLabel>
            <Select
              value={tarifa}
              onChange={(e) => {
                setSelectedField("tarifa");
                // @ts-expect-error
                setTarifa(JSON.parse(e.target.value) as Fee);
              }}
              renderValue={(tarifa) => {
                // @ts-expect-error
                const tarifaParse = JSON.parse(tarifa);
                return `${tarifaParse.moneda} ${tarifaParse.monto}`;
              }}
              labelId={tarifaLabelId}
              id={tarifaSelectId}
              label="Tarifa"
            >
              {tarifas.map((tarifa) => (
                <MenuItem key={tarifa.nombre} value={JSON.stringify(tarifa)}>
                  {tarifa.nombre} - {tarifa.moneda} {tarifa.monto}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Cambiar Cobro Adicional
          </Typography>
          <TextField
            sx={{ width: "80%" }}
            disabled={selectedField !== null && selectedField !== "cobroAdicional"}
            variant="outlined"
            size="small"
            type="number"
            value={cobroAdicional}
            onChange={(e) => {
              setSelectedField("cobroAdicional");
              setCobroAdicional(Number(e.target.value));
            }}
            label="Cobro Adicional"
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Cambiar Envío
            </Typography>
            {selectedField === "envio" && (
              <Typography variant="caption" color="text.secondary">
                (Dejar vacío para eliminar)
              </Typography>
            )}
          </Box>
          <TextField
            sx={{ width: "80%" }}
            disabled={selectedField !== null && selectedField !== "envio"}
            variant="outlined"
            size="small"
            value={envio}
            onClick={() => {
              setSelectedField("envio");
            }}
            onFocus={() => {
              setSelectedField("envio");
            }}
            onChange={(e) => {
              setSelectedField("envio");
              setEnvio(e.target.value);
            }}
            label="Nombre del envío"
            placeholder="Dejar vacío para eliminar el envío"
          />
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,.25)",
            borderRadius: 2,
            gap: 6,
            padding: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Cambiar Ubicación física
            </Typography>
            {selectedField === "ubicacionFisica" && (
              <Typography variant="caption" color="text.secondary">
                (Dejar vacío para eliminar)
              </Typography>
            )}
          </Box>
          <TextField
            sx={{ width: "80%" }}
            disabled={selectedField !== null && selectedField !== "ubicacionFisica"}
            variant="outlined"
            size="small"
            value={ubicacionFisica}
            onClick={() => {
              setSelectedField("ubicacionFisica");
            }}
            onFocus={() => {
              setSelectedField("ubicacionFisica");
            }}
            onChange={(e) => {
              setSelectedField("ubicacionFisica");
              setUbicacionFisica(e.target.value);
            }}
            label="Ubicación física"
            placeholder="Dejar vacío para eliminar la ubicación"
          />
        </Box>
      </Box>
      <Divider sx={{ marginTop: 2 }} />
      <Box display="flex" gap={2} p={2}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancelar
        </Button>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          onClick={() => handleBatchUpdate()}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </Button>
      </Box>
    </>
  );
}
